/**
 * Which screen a person may open, from what `my_access()` says (PLAN-MULTI-TENANT § 10.2, lot L6g). The route guard,
 * the menus and the home page all ask `canOpen`: one rule, read by every surface (principle 11).
 *
 * A screen is opened by a data domain when what it reads and writes is open to that domain in the base (lot L6e: the
 * accounts, contacts, opportunities, notes, tasks and activities of the CRM). Every other screen of `/admin` stays with
 * the administrators: a path that no rule names is an administrator's (fail closed), so a new screen is hidden from a
 * member until it is opened here, once the base opens what it reads. The appointments' page reads them through an Edge
 * Function that checks an administrator (appointments-manage): it waits for that function, the appointments of an
 * account show on its page meanwhile.
 */

import type { MyAccess } from './myAccess';

/** What a screen needs: an approved member of the team, or one of the data domains. */
export type ScreenNeed = { readonly kind: 'member' } | { readonly kind: 'domain'; readonly domain: string };

interface ScreenRule {
  /** A path prefix, matched on whole segments (`/admin/crm/accounts` opens `/admin/crm/accounts/42`). */
  readonly prefix: string;
  readonly need: ScreenNeed;
  /** The path itself only, not what is below it (a redirect whose siblings stay closed). */
  readonly exact?: boolean;
}

const CRM: ScreenNeed = { kind: 'domain', domain: 'crm' };

/** The screens a member opens without being an administrator. The longest matching prefix decides. */
export const SCREEN_RULES: readonly ScreenRule[] = [
  { prefix: '/admin/profile', need: { kind: 'member' } },
  // `/admin/crm` leads to the opportunities; the duplicates, the Google sync and the groups below it stay closed.
  { prefix: '/admin/crm', need: CRM, exact: true },
  { prefix: '/admin/crm/opportunities', need: CRM },
  { prefix: '/admin/crm/accounts', need: CRM },
  { prefix: '/admin/crm/carte', need: CRM },
  { prefix: '/admin/crm/company', need: CRM },
  { prefix: '/admin/crm/contacts', need: CRM },
  { prefix: '/admin/crm/leads', need: CRM },
];

/** The rule of a path: the longest prefix that matches it on whole segments, or none (an administrator's). Pure. */
export function screenNeed(pathname: string): ScreenNeed | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  let best: ScreenRule | null = null;
  for (const rule of SCREEN_RULES) {
    const matches = path === rule.prefix || (rule.exact !== true && path.startsWith(`${rule.prefix}/`));
    if (matches && (best === null || rule.prefix.length > best.prefix.length)) best = rule;
  }
  return best?.need ?? null;
}

/** Whether the person may open the path: an administrator opens every screen; a member, those a rule opens. Pure. */
export function canOpen(pathname: string, access: MyAccess): boolean {
  if (!access.isMember) return false;
  if (access.isAdmin) return true;
  const need = screenNeed(pathname);
  if (need === null) return false;
  return need.kind === 'member' || access.domains.includes(need.domain);
}

/** Where the person lands on `/admin`: the Bureau for an administrator, else the first screen they may open. Pure. */
export const ADMIN_HOME = '/admin/travail';
const MEMBER_HOMES = ['/admin/crm/opportunities', '/admin/profile'] as const;

export function homePath(access: MyAccess): string | null {
  if (!access.isMember) return null;
  if (access.isAdmin) return ADMIN_HOME;
  return MEMBER_HOMES.find((path) => canOpen(path, access)) ?? null;
}
